/******/ (() => { // webpackBootstrap
/******/ 	"use strict";

;// ./src/javascripts/utils.js
/**
 * Throttle execution of function
 *
 * @param {Function} callback function to be throttled
 * @param {number} interval milliseconds
 * @returns {Function}
 */
function throttle(callback, interval) {
  let enableCall = true;
  let calledWhenDisabled = false;


  return (...args) =>{
    const onTimeout = () => {
      if (calledWhenDisabled) {
        callback.apply(this, args);
        setTimeout(onTimeout, interval);
      } else {
        enableCall = true;
      }
      calledWhenDisabled = false;
    }

    if (!enableCall) {
      calledWhenDisabled = true;
      return;
    }

    enableCall = false;
    callback.apply(this, args);
    setTimeout(onTimeout, interval);
  }
}

/**
 * Take monetary value and format it as money
 *
 * @param {number | string} cents - Price in cents (hundreds of base unit)
 * @param {string} [format] - Format for money; store format will be used if undefined
 * @return {string} - HTML string with the formatted money value
 */
function formatMoney(cents, format) {
  var moneyFormat = format || '${{amount}}';
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  var value = '';
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = moneyFormat;

  function formatWithDelimiters(number, precision, thousands, decimal) {
    if (precision === null || precision === undefined) {
      precision = 2;
    }
    thousands = thousands || ',';
    decimal = decimal || '.';

    if (isNaN(number) || number == null) {
      return '0';
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split('.');
    var dollarsAmount = parts[0].replace(
      /(\d)(?=(\d{3})+(?!\d))/g,
      '$1' + thousands
    );
    var centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  var match = formatString.match(placeholderRegex);

  if (!match) {
    throw new Error(
      `Invalid format string: '${formatString}'. Expected '{{amount}}' or similar placeholders.`
    );
  }

  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ');
      break;
    default:
      throw new Error(`Unknown format type: ${match[1]}`);
  }

  return formatString.replace(placeholderRegex, value);
}

// FocusTrap class for managing focus within a container
class FocusTrap {
  /**
   * @param {HTMLElement} container - The container within which to trap focus.
   * @param {HTMLElement} [initialElement=null] - The element to focus initially. If not provided, the first focusable element is focused.
   * @param {Function} [onEscape=null] - Optional callback to execute when the Escape key is pressed.
   */
  constructor(container, initialElement = null, onEscape = null) {
    this.container = container;
    this.initialElement = initialElement;
    this.onEscape = onEscape;
    this.focusableElements = [];
    this.firstElement = null;
    this.lastElement = null;
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.updateFocusableElements = this.updateFocusableElements.bind(this);
    this.observer = new MutationObserver(this.updateFocusableElements);
    this.init();
  }

  /**
   * Initializes the focus trap by removing the 'inert' attribute,
   * updating focusable elements, setting initial focus, and attaching necessary event listeners.
   */
  init() {
    if (!this.container) return;

    // Remove the 'inert' attribute from the container
    this.container.removeAttribute('inert');

    this.updateFocusableElements();

    if (this.initialElement && this.isFocusable(this.initialElement)) {
      this.initialElement.focus();
    } else if (this.firstElement) {
      this.firstElement.focus();
    }

    this.container.addEventListener('keydown', this.handleKeyDown);
    this.observer.observe(this.container, { childList: true, subtree: true, attributes: true });
  }

  /**
   * Determines if an element is focusable.
   * @param {HTMLElement} el - The element to check.
   * @returns {boolean} - True if the element is focusable, else false.
   */
  isFocusable(el) {
    if (!el) return false;
    return (
      el.offsetParent !== null &&
      el.getAttribute('tabindex') !== '-1' &&
      !this.isInsideClosedDetails(el)
    );
  }

  /**
   * Determines if an element is inside a closed <details> element.
   * Allows <summary> elements to remain focusable even if their parent <details> is closed.
   * @param {HTMLElement} el - The element to check.
   * @returns {boolean} - True if inside a closed <details> and not a <summary>, else false.
   */
  isInsideClosedDetails(el) {
    let parent = el.parentElement;
    while (parent) {
      if (parent.tagName.toLowerCase() === 'details') {
        if (!parent.hasAttribute('open')) {
          // Allow the <summary> element itself to be focusable
          if (el.tagName.toLowerCase() === 'summary' && parent.querySelector('summary') === el) {
            return false;
          }
          return true;
        }
      }
      parent = parent.parentElement;
    }
    return false;
  }

  /**
   * Updates the list of focusable elements, excluding those inside closed <details> (except <summary>).
   */
  updateFocusableElements() {
    this.focusableElements = Array.from(this.container.querySelectorAll(
      'a[href], area[href], input:not([type=hidden]), select, textarea, button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable], summary'
    )).filter(el => this.isFocusable(el));

    if (this.focusableElements.length > 0) {
      this.firstElement = this.focusableElements[0];
      this.lastElement = this.focusableElements[this.focusableElements.length - 1];
    } else {
      this.firstElement = null;
      this.lastElement = null;
      console.warn('No focusable elements found within the container.');
    }
  }

  /**
   * Handles the keydown event to trap focus within the container.
   * @param {KeyboardEvent} e - The keyboard event.
   */
  handleKeyDown(e) {
    if (e.key === 'Tab') {
      if (this.focusableElements.length === 0) {
        e.preventDefault();
        return;
      }
      if (e.shiftKey) { // Shift + Tab
        if (document.activeElement === this.firstElement) {
          e.preventDefault();
          this.lastElement.focus();
        }
      } else { // Tab
        if (document.activeElement === this.lastElement) {
          e.preventDefault();
          this.firstElement.focus();
        }
      }
    } else if (e.key === 'Escape') {
      if (this.onEscape) {
        this.onEscape();
      }
    }
  }

  /**
   * Destroys the focus trap by removing event listeners and disconnecting observers.
   */
  destroy() {
    this.container.setAttribute('inert', '');
    this.container.removeEventListener('keydown', this.handleKeyDown);
    this.observer.disconnect();
  }
}

/**
 * CartManager - Centralized cart management utility for adding products to cart
 * Handles both single and multiple product additions with proper loading states,
 * cart drawer updates, and success messaging
 */
class CartManager {
  constructor() {
    this.cartType = document.getElementById('PageContainer')?.dataset.cartType;
    this.cartAction = document.getElementById('PageContainer')?.dataset.cartAction;
    this.languageUrl = document.getElementById('PageContainer')?.dataset.languageUrl;
    this.cartCountIndicator = document.querySelector('[data-cart-count-indicator]');
    
    // Get translations object
    this.wethemeGlobal = document.querySelector('script#wetheme-global');
    this.translations = this.wethemeGlobal ? JSON.parse(this.wethemeGlobal.textContent).translations : {};
  }

  /**
   * Add a single product to cart (equivalent to QuickAdd functionality)
   * @param {HTMLFormElement|FormData|Object} formOrData - Form element, FormData, or object with variant data
   * @param {HTMLElement} button - Button element for loading states
   * @param {Object} options - Additional options
   * @returns {Promise<Response>}
   */
  async addSingleProduct(formOrData, button, options = {}) {
    let variantId, quantity = 1;
    
    // Extract variant data based on input type
    if (formOrData instanceof HTMLFormElement) {
      const formData = new FormData(formOrData);
      variantId = formData.get('id');
      quantity = parseInt(formData.get('quantity') || '1', 10);
    } else if (formOrData instanceof FormData) {
      variantId = formOrData.get('id');
      quantity = parseInt(formOrData.get('quantity') || '1', 10);
    } else if (typeof formOrData === 'object') {
      variantId = formOrData.id || formOrData.variantId;
      quantity = formOrData.quantity || 1;
    }

    if (!variantId) {
      throw new Error('No variant ID provided');
    }

    const items = [{
      id: parseInt(variantId, 10),
      quantity
    }];

    return this.addItemsToCart(items, button, options);
  }

  /**
   * Add multiple products to cart
   * @param {Array} items - Array of {id, quantity} objects
   * @param {HTMLElement} button - Button element for loading states
   * @param {Object} options - Additional options
   * @returns {Promise<Response>}
   */
  async addMultipleProducts(items, button, options = {}) {
    if (!Array.isArray(items) || items.length === 0) {
      throw new Error('No items provided or invalid items array');
    }

    return this.addItemsToCart(items, button, options);
  }

  /**
   * Core method to add items to cart with fallback to individual additions
   * @param {Array} items - Array of {id, quantity} objects
   * @param {HTMLElement} button - Button element for loading states
   * @param {Object} options - Additional options
   * @returns {Promise<Response>}
   */
  async addItemsToCart(items, button, options = {}) {
    if (button) {
      this.showLoadingState(button);
    }

    try {
      // Try bulk add first
      const response = await this.performBulkAdd(items);
      
      if (response.ok) {
        await this.handleSuccess(button, response, options);
        return response;
      } else if (response.status === 422) {
        // Inventory error - try individual adds
        console.warn('Bulk add failed due to inventory constraints, trying individual adds...');
        const individualResponse = await this.addItemsIndividually(items);
        await this.handleSuccess(button, individualResponse, options);
        return individualResponse;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add items to cart');
      }
    } catch (error) {
      // If bulk add fails, try individual adds
      console.warn('Bulk add failed, trying individual adds...', error);
      try {
        const individualResponse = await this.addItemsIndividually(items);
        await this.handleSuccess(button, individualResponse, options);
        return individualResponse;
      } catch (individualError) {
        if (button) {
          this.hideLoadingState(button);
        }
        throw individualError;
      }
    }
  }

  /**
   * Perform bulk cart addition
   * @param {Array} items - Array of {id, quantity} objects
   * @returns {Promise<Response>}
   */
  async performBulkAdd(items) {
    const payload = { items };
    
    // Add sections parameter for cart drawer
    if (this.cartType === 'drawer') {
      payload.sections = 'cart-drawer';
    }
    
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    };
    
    return fetch(window.routes.cart_add_url, config);
  }

  /**
   * Add items individually with error handling
   * @param {Array} items - Array of {id, quantity} objects
   * @returns {Promise<Response>}
   */
  async addItemsIndividually(items) {
    let lastSuccessfulResponse = null;
    let successfullyAdded = 0;
    let skippedItems = 0;

    for (const item of items) {
      try {
        const payload = { items: [item] };
        
        if (this.cartType === 'drawer') {
          payload.sections = 'cart-drawer';
        }
        
        const config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        };
        
        const response = await fetch(window.routes.cart_add_url, config);
        
        if (response.ok) {
          successfullyAdded++;
          lastSuccessfulResponse = response;
        } else if (response.status === 422) {
          skippedItems++;
          console.warn(`Skipping item ${item.id} due to inventory constraints`);
        } else {
          const errorData = await response.json();
          console.error(`Failed to add item ${item.id}:`, errorData.description || 'Unknown error');
          skippedItems++;
        }
      } catch (error) {
        console.error(`Error adding item ${item.id}:`, error);
        skippedItems++;
      }
    }

    if (successfullyAdded === 0) {
      throw new Error('No items could be added to cart');
    }

    if (skippedItems > 0) {
      console.info(`Successfully added ${successfullyAdded} items, skipped ${skippedItems} items due to inventory or other issues`);
    }

    return lastSuccessfulResponse || { ok: true, status: 200 };
  }

  /**
   * Handle successful cart addition
   * @param {HTMLElement} button - Button element
   * @param {Response} response - Fetch response
   * @param {Object} options - Additional options
   */
  async handleSuccess(button, response, options = {}) {
    if (button) {
      this.hideLoadingState(button);
    }

    // Update cart drawer if needed
    if (this.cartType === 'drawer' && response && response.ok) {
      try {
        const responseJson = await response.clone().json();
        this.updateCartDrawer(responseJson);
      } catch (error) {
        // Response might already be consumed, continue without drawer update
        console.warn('Could not parse response for cart drawer update:', error);
      }
    }

    // Emit cart added event
    if (window.eventBus && options.sectionId) {
      window.eventBus.emit('cart:added', { sectionId: options.sectionId });
    }

    // Handle cart action and update count
    if (button) {
      this.handleCartAction(button);
    } else {
      this.updateCartCountIndicator();
    }
  }

  /**
   * Update cart drawer
   * @param {Object} responseJson - Cart response data
   */
  updateCartDrawer(responseJson) {
    if (window.eventBus) {
      window.eventBus.emit('update:cart:drawer', responseJson);
    }
  }

  /**
   * Handle cart action based on settings
   * @param {HTMLElement} button - Button element
   */
  handleCartAction(button) {
    if (this.cartType === 'drawer') {
      if (this.cartAction === 'show_added_message') {
        this.showAddedMessage(button);
        this.updateCartCountIndicator();
      } else if (this.cartAction === 'go_to_or_open_cart') {
        if (window.eventBus) {
          window.eventBus.emit('open:cart:drawer', { scrollToTop: true });
        }
        this.updateCartCountIndicator();
      }
    } else {
      if (this.cartAction === 'show_added_message') {
        this.showAddedMessage(button);
        this.updateCartCountIndicator();
      } else {
        window.location = window.routes.cart_url;
      }
    }
  }

  /**
   * Show "Added" message on button
   * @param {HTMLElement} button - Button element
   */
  showAddedMessage(button) {
    const addedTranslation = this.translations.added;
    
    // Check if this button uses the dynamic-section-button structure
    const buttonText = button.querySelector('[data-button-text]');
    const addedText = button.querySelector('[data-cart-added-text]');
    
    if (buttonText && addedText) {
      // Dynamic section button with added text support
      buttonText.style.visibility = 'hidden';
      addedText.style.display = 'block';
      
      setTimeout(() => {
        addedText.style.display = 'none';
        buttonText.style.visibility = '';
        button.disabled = false;
      }, 2000);
    } else if (buttonText) {
      // Dynamic section button without added text - create temporary overlay
      buttonText.style.visibility = 'hidden';
      
      const tempAddedText = document.createElement('span');
      tempAddedText.textContent = addedTranslation;
      tempAddedText.style.gridColumn = '1';
      tempAddedText.style.gridRow = '1';
      tempAddedText.classList.add('temp-added-text');
      button.appendChild(tempAddedText);
      
      setTimeout(() => {
        buttonText.style.visibility = '';
        tempAddedText.remove();
        button.disabled = false;
      }, 2000);
    } else {
      // Regular button - hide original content and overlay "Added" text
      const originalHTML = button.dataset.originalText || button.innerHTML;
      
      const originalContent = button.innerHTML;
      button.innerHTML = '';
      
      const hiddenContent = document.createElement('span');
      hiddenContent.innerHTML = originalContent;
      hiddenContent.style.visibility = 'hidden';
      hiddenContent.style.gridColumn = '1';
      hiddenContent.style.gridRow = '1';
      
      const addedContent = document.createElement('span');
      addedContent.textContent = addedTranslation;
      addedContent.style.gridColumn = '1';
      addedContent.style.gridRow = '1';
      addedContent.classList.add('temp-added-text');
      
      button.appendChild(hiddenContent);
      button.appendChild(addedContent);
      
      setTimeout(() => {
        button.innerHTML = originalHTML;
        button.disabled = false;
      }, 2000);
    }
  }

  /**
   * Update cart count indicator
   */
  async updateCartCountIndicator() {
    if (!this.cartCountIndicator) return;
    
    try {
      const languageParam = !this.languageUrl || this.languageUrl === '/' ? '' : this.languageUrl;
      const response = await fetch(`${languageParam}/cart?view=compare`);
      const cart = await response.json();
      if (window.wetheme?.updateCartCount) {
        window.wetheme.updateCartCount(cart);
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /**
   * Show loading state on button
   * @param {HTMLElement} button - Button element
   */
  showLoadingState(button) {
    button.disabled = true;
    button.classList.add('loading');
    
    const buttonText = button.querySelector('[data-button-text]');
    const loadingIcon = button.querySelector('[data-loading-icon]');
    
    if (buttonText && loadingIcon) {
      // Dynamic section button with loading icon structure
      buttonText.style.visibility = 'hidden';
      loadingIcon.classList.remove('hidden');
    } else {
      // Regular button - store original text and show loading icon
      if (!button.dataset.originalText) {
        button.dataset.originalText = button.textContent;
      }
      button.innerHTML = `<svg viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="1.6em" height="1.6em" class="spin flex-full"><g clip-path="url(#clip0_3605_47041)"><path d="M12.5 23C6.42487 23 1.5 18.0751 1.5 12C1.5 5.92487 6.42487 1 12.5 1C18.5751 1 23.5 5.92487 23.5 12C23.5 15.1767 22.1534 18.0388 20 20.0468" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></g><defs><clipPath id="clip0_3605_47041"><rect width="24" height="24" fill="none" transform="translate(0.5)"/></clipPath></defs></svg>`;
    }
  }

  /**
   * Hide loading state on button
   * @param {HTMLElement} button - Button element
   */
  hideLoadingState(button) {
    button.disabled = false;
    button.classList.remove('loading');
    
    const buttonText = button.querySelector('[data-button-text]');
    const loadingIcon = button.querySelector('[data-loading-icon]');
    
    if (buttonText && loadingIcon) {
      // Dynamic section button with loading icon structure
      buttonText.style.visibility = '';
      loadingIcon.classList.add('hidden');
    } else {
      // Regular button - restore original text
      if (button.dataset.originalText) {
        button.innerHTML = button.dataset.originalText;
      }
    }
  }
}

;// ./src/javascripts/webcomponents/filtering.js


// Show More / Show Less state per Designs & Colors group, keyed by the toggle's
// data-state-key. Module scope so it outlives the facet markup every AJAX render
// replaces.
const valueGroupsState = new Map();

class CollectionFiltersForm extends HTMLElement {
  // Define class properties for bound functions
  handleSortByChangeBound = this.handleSortByChange.bind(this);
  stopInputPropagationBound = this.stopInputPropagation.bind(this);

  constructor() {
    super();
    this.filterData = [];
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onHistoryChangeBound = this.onHistoryChange.bind(this);
    this.onDropDownBlurBound = this.handleDropDownBlur.bind(this);
    this.productGridId = 'CollectionProductGrid';
    this.filterFormsId = 'CollectionFiltersForm';
    this.searchTemplate = document.querySelector('body.template-search');
    this.collectionTemplate = document.querySelector('body.template-collection');
    this.sections = this.getSections();
    this.buttonAriaLabel = this.getAttribute('data-button-aria-label');
    this.filterForm = this.querySelector(`#${this.filterFormsId}`);
    this.sortByDesktop = this.querySelector('#SortBydesktop');
    this.sortByMobile = this.querySelector('#SortBymobile');
    this.sortByInput = this.querySelector('#sort_by_input');
    
    // Store initial window width to detect significant resize
    this.prevWindowWidth = window.innerWidth;
    this.resizeThreshold = 100; // Only care about resizes larger than this
    this.mobileBreakpoint = 767;

    // Set by onSubmitHandler when a Design/Color swatch triggers the change, consumed
    // by renderFilters() once the re-render lands, so the mobile drawer closes straight
    // to results instead of waiting for a manual close (matches desktop's auto-collapse).
    this.pendingMobileAutoClose = false;

    // Setup proper inert attribute only on initial page load
    if (this.isMobileViewport()) {
      // On mobile, only set inert when drawer is closed
      if (!document.body.classList.contains('js-drawer-open-filter')) {
        this.filterForm.setAttribute('inert', '');
      }
    } else {
      // On desktop, form should never be inert
      this.filterForm.removeAttribute('inert');
    }

    this.debouncedOnSubmit = this.debounce(this.onSubmitHandler.bind(this), 800);
    
    // Bind this for the resize handler for proper context
    this.handleResize = this.handleResize.bind(this);
    
    // Bind filter event handler
    this.handleCollectionFilterApply = this.handleCollectionFilterApply.bind(this);

    // Add event listeners using bound functions
    if (this.sortByDesktop && this.sortByMobile) {
      this.sortByDesktop.addEventListener('change', this.handleSortByChangeBound);
      this.sortByMobile.addEventListener('change', this.handleSortByChangeBound);

      this.sortByDesktop.addEventListener('input', this.stopInputPropagationBound);
      this.sortByMobile.addEventListener('input', this.stopInputPropagationBound);
    }

    const filterFormElement = this.querySelector('form');
    if (filterFormElement) {
      filterFormElement.addEventListener('input', this.debouncedOnSubmit);
    }

    const openBtn = document.querySelector('[data-drawer-open-btn]');
    if (openBtn) {
      openBtn.addEventListener('click', this.handleDrawerOpen.bind(this));
    }

    const closeBtn = document.querySelector('[data-drawer-close-btn]');
    if (closeBtn) {
      closeBtn.addEventListener('click', this.handleDrawerClose.bind(this));
    }

    const drawerOverlay = document.querySelector('[data-filtering-drawer-overlay]');
    if (drawerOverlay) {
      drawerOverlay.addEventListener('click', this.handleDrawerClose.bind(this));
    }
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('popstate', this.onHistoryChangeBound);

    // Listen for collection filter apply events
    if (window.eventBus) {
      window.eventBus.on('collection:filter:apply', this.handleCollectionFilterApply);
    }

    this.colorDesignLinks = this.readColorDesignLinks();
    this.applyColorDesignLinksOnLoad();

    this.renderActiveFacetFallback();
    this.bindActiveFacetButtonEvents();
    this.onDropDownBlur();

    // Delegated on the element itself: the toggles are re-rendered with the facets.
    this.addEventListener('click', this.onValueGroupToggleClick.bind(this));
    this.layoutValueGroups();
  }

  /**
   * AC-1986: a Designs & Colors group longer than its toggle's data-visible shows
   * only its first values until "Show More". Runs after every facet render: the
   * collection page swaps the lists and the search page rewrites each item's
   * className, so the hide class never survives one. It also has to run after
   * hideImpliedFacets(), which unchecks the designs a color only implies, because
   * a checked value past the cut stays visible in the collapsed view.
   */
  layoutValueGroups() {
    this.querySelectorAll('[data-value-group-toggle]').forEach((toggle) => {
      const list = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!list) return;

      const limit = parseInt(toggle.dataset.visible, 10) || 6;
      const overflow = Array.from(list.children).filter(
        (item, index) => index >= limit && !item.querySelector('input[type="checkbox"]:checked')
      );
      const hasMore = overflow.length > 0;
      const expanded = hasMore && valueGroupsState.get(toggle.dataset.stateKey) === true;

      Array.from(list.children).forEach((item) => item.classList.remove('is-group-hidden'));
      if (!expanded) overflow.forEach((item) => item.classList.add('is-group-hidden'));

      toggle.hidden = !hasMore;
      toggle.setAttribute('aria-expanded', String(expanded));
      // Only the label span: the button also holds the chevron icon.
      const label = toggle.querySelector('[data-value-group-toggle-label]') || toggle;
      label.textContent = expanded ? toggle.dataset.labelLess : toggle.dataset.labelMore;
    });
  }

  onValueGroupToggleClick(event) {
    const toggle = event.target.closest('[data-value-group-toggle]');
    if (!toggle) return;

    const expanded = toggle.getAttribute('aria-expanded') !== 'true';
    valueGroupsState.set(toggle.dataset.stateKey, expanded);
    this.layoutValueGroups();

    // Collapsing a long group can leave the button outside the dropdown's or the
    // drawer's scroll area.
    if (!expanded) toggle.scrollIntoView({ block: 'nearest' });
  }

  /**
   * Color → Design links from the `color_design_reference` metaobject
   * (snippets/color-design-links.liquid). Colors and designs are values of the same
   * Designs option: choosing Black must also list the Bling Bonz products. Shopify
   * ORs the values of one filter, so the linked designs are added to every request
   * we make, while the URL, the checkboxes and the chips only ever show what the
   * shopper picked. A linked design is "implied" whenever one of its colors is
   * selected; selecting it on top of the color changes nothing, so it is treated
   * the same way.
   */
  readColorDesignLinks() {
    const source = document.querySelector('script[data-color-design-links]');
    if (!source) return null;

    try {
      const data = JSON.parse(source.textContent);
      const byColor = new Map();
      (data.links || []).forEach(({ design, colors }) => {
        (colors || []).forEach((color) => {
          if (!byColor.has(color)) byColor.set(color, new Set());
          byColor.get(color).add(design);
        });
      });
      return byColor.size ? { param: data.param, byColor } : null;
    } catch (error) {
      console.error('color-design-links: could not read the metaobject data', error);
      return null;
    }
  }

  // The designs implied by the colors selected in any of the given query strings.
  impliedDesigns(...queries) {
    const implied = new Set();
    if (!this.colorDesignLinks) return implied;

    const { param, byColor } = this.colorDesignLinks;
    queries.forEach((query) => {
      new URLSearchParams(query).getAll(param).forEach((value) => {
        (byColor.get(value) || []).forEach((design) => implied.add(design));
      });
    });
    return implied;
  }

  /**
   * What the shopper actually selected: implied designs dropped. Relative to the
   * current URL as well as the target, so a chip whose url_to_remove came back from
   * an expanded response (Black chip → `designs=Bling Bonz`) does not leave the
   * implied design behind once its color is gone.
   */
  canonicalFilterParams(searchParams) {
    if (!this.colorDesignLinks) return searchParams;

    const implied = this.impliedDesigns(window.location.search, searchParams);
    if (!implied.size) return searchParams;

    const { param } = this.colorDesignLinks;
    const params = new URLSearchParams(searchParams);
    const kept = params.getAll(param).filter((value) => !implied.has(value));
    params.delete(param);
    kept.forEach((value) => params.append(param, value));
    return params.toString();
  }

  // What we ask Shopify for: the selection plus the designs its colors imply.
  expandedFilterParams(searchParams) {
    if (!this.colorDesignLinks) return searchParams;

    const implied = this.impliedDesigns(searchParams);
    if (!implied.size) return searchParams;

    const { param } = this.colorDesignLinks;
    const params = new URLSearchParams(searchParams);
    const present = new Set(params.getAll(param));
    implied.forEach((design) => {
      if (!present.has(design)) params.append(param, design);
    });
    return params.toString();
  }

  /**
   * The server rendered the page for the URL it was given. Landing on a plain
   * `?designs=Black` link, that is Black alone: fetch the grid again with the
   * linked designs. Landing on a link that already carries them (a paginator href
   * from an expanded response), the grid is right and only the URL and the facets
   * need tidying.
   */
  applyColorDesignLinksOnLoad() {
    if (!this.colorDesignLinks) return;

    const current = window.location.search.slice(1);
    const canonical = this.canonicalFilterParams(current);
    const expanded = this.expandedFilterParams(canonical);
    if (expanded === canonical) return;

    if (canonical !== current) {
      history.replaceState({ searchParams: canonical }, '', `${window.location.pathname}${canonical ? `?${canonical}` : ''}`);
    }

    if (this.sameParams(expanded, current)) {
      this.hideImpliedFacets(current);
    } else {
      this.renderPage(canonical, null, false);
    }
  }

  sameParams(a, b) {
    const sorted = (query) => Array.from(new URLSearchParams(query).entries()).map(String).sort().join('&');
    return sorted(a) === sorted(b);
  }

  /**
   * The response was rendered for the expanded query, so Shopify marks the implied
   * designs active: checked, counted, and given a chip. Undo that in the DOM so the
   * facets show the shopper's own selection.
   */
  hideImpliedFacets(renderedParams) {
    if (!this.colorDesignLinks) return;

    const implied = this.impliedDesigns(renderedParams);
    if (!implied.size) return;

    const { param } = this.colorDesignLinks;
    const rendered = new URLSearchParams(renderedParams).getAll(param);

    this.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      if (input.name === param && implied.has(input.value)) input.checked = false;
    });

    // A chip's href is url_to_remove: the rendered query minus its own value.
    document.querySelectorAll('.active-facets .js-facet-remove.button--tertiary:not(.price-filter-reset)').forEach((chip) => {
      let values;
      try {
        values = new URL(chip.getAttribute('href'), window.location.origin).searchParams.getAll(param);
      } catch (error) {
        return;
      }
      const missing = rendered.filter((value) => !values.includes(value));
      if (missing.length === 1 && implied.has(missing[0])) chip.remove();
    });

    this.querySelectorAll('details.js-filter').forEach((details) => {
      if (details.dataset.filterKey !== param) return;

      const count = details.querySelectorAll(`input[type="checkbox"]:checked`).length;
      const bubble = details.querySelector('.count-bubble');
      if (bubble) bubble.textContent = count > 0 ? String(count) : '';
      const selected = details.querySelector('.facets__selected');
      if (selected) selected.textContent = selected.textContent.replace(/\d+/, String(count));
    });
  }

  // Define debounce as a class method
  debounce = (fn, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  };

  // Define stopInputPropagation as a class method
  stopInputPropagation(event) {
    event.stopPropagation();
  }

  handleDropDownBlur(event) {
    const openDropDowns = document.querySelectorAll('details.facets__disclosure[open]');
    
    openDropDowns.forEach(dropdown => {
      if (!dropdown.contains(event.target)) {
        // The click was outside the specified element, close the dropdown
        dropdown.removeAttribute('open');
      }
    });
  }

  onDropDownBlur() {
    // Single delegated listener for better performance
    document.addEventListener('click', this.onDropDownBlurBound);
  }

  isHidden(el) {
    return (el.offsetParent === null);
  }

  isMobileViewport() {
    return window.innerWidth <= this.mobileBreakpoint;
  }

  handleResize() {
    // Only react to significant resize events
    const currentWidth = window.innerWidth;
    const widthDiff = Math.abs(currentWidth - this.prevWindowWidth);
    
    // Check if the resize is significant (crossing the mobile/desktop threshold)
    const crossedBreakpoint =
      (this.prevWindowWidth <= this.mobileBreakpoint && currentWidth > this.mobileBreakpoint) ||
      (this.prevWindowWidth > this.mobileBreakpoint && currentWidth <= this.mobileBreakpoint);
    
    if (widthDiff > this.resizeThreshold || crossedBreakpoint) {
      // Update previous width
      this.prevWindowWidth = currentWidth;
      
      const filterDrawer = document.querySelector('collection-filtering-form');
      
      if (!filterDrawer) return;
      
      // Handle drawer attributes
      filterDrawer.removeAttribute('role');
      filterDrawer.removeAttribute('aria-modal');
      filterDrawer.removeAttribute('aria-label');
      
      // Manage inert attribute properly
      if (currentWidth <= this.mobileBreakpoint) {
        // On mobile - only set inert if drawer is closed
        if (!document.body.classList.contains('js-drawer-open-filter')) {
          this.filterForm.setAttribute('inert', '');
        }
        
        // Set drawer attributes
        filterDrawer.setAttribute('role', 'dialog');
        filterDrawer.setAttribute('aria-modal', 'true');
        filterDrawer.setAttribute('aria-label', this.buttonAriaLabel);
      } else {
        // On desktop - never use inert
        this.filterForm.removeAttribute('inert');
      }
    }
  }

  handleCollectionFilterApply(event) {
    if (!event || !event.filterParams) return;
    
    // Apply the filter by rendering the page with new parameters
    this.renderPage(event.filterParams, null, true);
  }

  handleDrawerOpen() {
    const filterDrawer = this.querySelector('#CollectionFiltersForm');
    
    // Remove inert attribute when drawer opens on mobile
    if (this.isMobileViewport() && this.filterForm) {
      this.filterForm.removeAttribute('inert');
    }
  
    document.body.classList.add('js-drawer-open-filter', 'js-drawer-open');
    this.focusTrap = new FocusTrap(filterDrawer, null, this.handleDrawerClose.bind(this));
  }
  
  handleDrawerClose() {
    const filterBtn = document.querySelector('[data-drawer-open-btn]');
    document.body.classList.remove('js-drawer-open-filter', 'js-drawer-open');
    
    // Only set inert attribute on mobile when drawer closes
    if (this.isMobileViewport() && this.filterForm) {
      this.filterForm.setAttribute('inert', '');
    }
  
    filterBtn.setAttribute('tabIndex', '0');
    filterBtn.focus();
  
    if (this.focusTrap) {
      this.focusTrap.destroy();
      this.focusTrap = null;
    }
  }

  handleSortByChange(event) {
    event.preventDefault();
    event.stopPropagation();

    const newValue = event.target.value;

    if (!this.sortByInput) {
      console.warn('Sort by input element not found');
      return;
    }

    // Update the hidden input
    this.sortByInput.value = newValue;

    // Determine which select was changed and update the other accordingly
    if (event.target === this.sortByDesktop) {
      // Update SortBymobile without triggering its change event
      if (this.sortByMobile.value !== newValue) {
        this.sortByMobile.value = newValue;
      }
    } else if (event.target === this.sortByMobile) {
      // Update SortBydesktop without triggering its change event
      if (this.sortByDesktop.value !== newValue) {
        this.sortByDesktop.value = newValue;
      }
    }

    // Dispatch the input event to trigger form's input listener
    this.sortByInput.dispatchEvent(new Event('input', { bubbles: true }));
  }

  onSubmitHandler(event) {
    event.preventDefault();

    this.pendingMobileAutoClose = this.isMobileViewport() &&
      !!(event.target && event.target.matches && event.target.matches('[data-autoclose-swatch="true"]'));

    const processForm = (formData, uniqueFormData) => {
      // Track price filter values to sync mobile and desktop
      let priceGte = null;
      let priceLte = null;

      // If this event was triggered by a price input, use its value
      if (event.target && event.target.matches('input[data-price-range-min]')) {
        priceGte = event.target.value;
      } else if (event.target && event.target.matches('input[data-price-range-max]')) {
        priceLte = event.target.value;
      }

      // Create a map to store unique values for each parameter
      const paramMap = new Map();

      // First collect all form values into our map
      for (const [key, value] of formData.entries()) {
        if (value === '') continue;

        // For checkboxes, we need to maintain an array of values
        if (!paramMap.has(key)) {
          paramMap.set(key, new Set());
        }
        paramMap.get(key).add(value);
      }

      // Handle checkbox unchecking
      if (event.target?.type === 'checkbox' && !event.target.checked) {
        if (paramMap.has(event.target.name)) {
          paramMap.get(event.target.name).delete(event.target.value);
          if (paramMap.get(event.target.name).size === 0) {
            paramMap.delete(event.target.name);
          }
        }
      }

      // Special handling for price filters
      if (priceGte !== null) {
        paramMap.set('filter.v.price.gte', new Set([priceGte]));
        this.filterForm.querySelectorAll('input[data-price-range-min]')
          .forEach(input => input.value = priceGte);
      }
      if (priceLte !== null) {
        paramMap.set('filter.v.price.lte', new Set([priceLte]));
        this.filterForm.querySelectorAll('input[data-price-range-max]')
          .forEach(input => input.value = priceLte);
      }

      // Build final URL parameters
      for (const [key, values] of paramMap.entries()) {
        // Delete any existing values for this key
        uniqueFormData.delete(key);
        // Add each value for this key
        values.forEach(value => {
          uniqueFormData.append(key, value);
        });
      }
    };

    if (this.collectionTemplate) {
      const formData = new FormData(this.filterForm);
      const uniqueFormData = new URLSearchParams();
      
      processForm(formData, uniqueFormData);
      const searchParams = uniqueFormData.toString();
      this.renderPage(searchParams, event);
    } else if (this.searchTemplate) {
      // Store the initial URL so that we can get the search query from it to add to the formData object
      const searchParamsInitial = new URLSearchParams(window.location.search);

      // Get the selected result types, we will need to use them later on
      const activeTypes = document.querySelector('#page-type-input').value;
      const types = Array.from(activeTypes.split(','));
      const defaultTypes = document.querySelector('.search--header input[name="type"]');

      if (activeTypes === "") {
        const getInput = document.querySelector('#page-type-input');
        getInput.value = defaultTypes.value;
      }

      // Create a new FormData object from the filter form
      const formData = new FormData(this.filterForm);
      const uniqueFormData = new URLSearchParams();
      
      processForm(formData, uniqueFormData);
      uniqueFormData.append('q', searchParamsInitial.get('q'));

      // If the result type is not of type product, we want to disable the filter inputs and remove product type queries from the URL
      if ((types.includes('article') && !types.includes('product')) || (types.includes('page') && !types.includes('product'))) {
        const getInputs = document.querySelectorAll(`#${this.filterFormsId} input:not(#page-type-input)`);
        const getLabels = document.querySelectorAll(`#${this.filterFormsId} label:not(.collection-filters__label)`);

        for (let item of getInputs) {
          item.setAttribute('disabled', 'disabled');
        }

        for (let inputLabel of getLabels) {
          inputLabel.classList.add('facet-checkbox--disabled');
        }

        if (searchParamsInitial.has('filter.p.product_type')) {
          uniqueFormData.delete('filter.p.product_type');
        }

        if (searchParamsInitial.has('filter.p.vendor')) {
          uniqueFormData.delete('filter.p.vendor');
        }

        if (searchParamsInitial.has('filter.v.availability')) {
          uniqueFormData.delete('filter.v.availability');
        }

        if (searchParamsInitial.has('filter.v.price.gte')) {
          uniqueFormData.delete('filter.v.price.gte');
        }

        if (searchParamsInitial.has('filter.v.price.lte')) {
          uniqueFormData.delete('filter.v.price.lte');
        }
      } else {
        // Re-enable the filter inputs
        const getInputs = document.querySelectorAll(`#${this.filterFormsId} input:not(#page-type-input)`);
        const getLabels = document.querySelectorAll(`#${this.filterFormsId} label:not(.collection-filters__label)`);

        for (let item of getInputs) {
          item.removeAttribute('disabled');
        }

        for (let inputLabel of getLabels) {
          inputLabel.classList.remove('facet-checkbox--disabled');
        }
      }

      const searchParams = uniqueFormData.toString();
      this.renderPage(searchParams, event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    event.currentTarget.setAttribute('loading', 'true');
    this.toggleActiveFacets();

    // Clear the price filters
    if (event.currentTarget.classList.contains('price-filter-reset') || event.currentTarget.classList.contains('js-clear-all-filters')) {
      this.filterForm.querySelectorAll('input[data-price-range-min]').forEach(input => input.value = '');
      this.filterForm.querySelectorAll('input[data-price-range-max]').forEach(input => input.value = '');
    }

    this.renderPage(new URL(event.currentTarget.href).searchParams.toString(), event);
  }

  onHistoryChange(event) {
    const searchParams = event.state?.searchParams ?? window.location.search.slice(1);
    this.renderPage(searchParams, null, false);
  }

  toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  renderPage(searchParams, event, updateURLHash = true) {
    document.querySelector(`#${this.productGridId}>div`).classList.add('loading');

    // The URL keeps the shopper's selection; the request also carries the designs
    // its colors imply (see readColorDesignLinks).
    searchParams = this.canonicalFilterParams(searchParams);
    const requestParams = this.expandedFilterParams(searchParams);
    this.renderedFilterParams = requestParams;

    this.sections.forEach((section) => {
      if (this.collectionTemplate) {
        const url = `${window.location.pathname}?section_id=${section.section}&${requestParams}`;
        const filterDataUrl = element => element.url === url;

        this.filterData.some(filterDataUrl) ?
          this.renderSectionFromCache(filterDataUrl, section, event) :
          this.renderSectionFromFetch(url, section, event);
      } else if (this.searchTemplate) {
        const url = `${window.location.pathname}?${requestParams}`;
        const filterDataUrl = element => element.url === url;

        this.filterData.some(filterDataUrl) ?
          this.renderSectionFromCache(filterDataUrl, section, event) :
          this.renderSectionFromFetch(url, section, event);
      }
    });

    if (updateURLHash) this.updateURLHash(searchParams);
    
    window.eventBus.emit('collection:filters:change', {details: {
      parameters: searchParams
    }});
  }

  renderSectionFromFetch(url, section, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        this.filterData = [...this.filterData, { html, url }];

        switch (section.id) {
          case this.productGridId:
            this.renderProductGrid(html);
            break;
          case this.filterFormsId:
            this.renderFilters(html, event);
            break;
          case 'CollectionGridBanner':
            this.renderCollectionGridBanner(html);
            break;
          default:
            return;
        }
      });
  }

  renderSectionFromCache(filterDataUrl, section, event) {
    const cachedData = this.filterData.find(filterDataUrl);
    if (!cachedData) return;

    const html = cachedData.html;
    
    switch (section.id) {
      case this.productGridId:
        this.renderProductGrid(html);
        break;
      case this.filterFormsId:
        this.renderFilters(html, event);
        break;
      case 'CollectionGridBanner':
        this.renderCollectionGridBanner(html);
        break;
      default:
        return;
    }
  }

  renderCollectionGridBanner(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newBannerElement = doc.querySelector('collection-grid-banner');
    const currentBannerElement = document.querySelector('collection-grid-banner');
    
    if (newBannerElement && currentBannerElement) {
      // Get all banner items from both current and new content
      const currentItems = currentBannerElement.querySelectorAll('.collection-grid-banner__item-image');
      const newItems = newBannerElement.querySelectorAll('.collection-grid-banner__item-image');
      
      // Replace each current item with its corresponding new item
      currentItems.forEach((currentItem, index) => {
        if (newItems[index]) {
          // Replace the current item with the new item
          currentItem.replaceWith(newItems[index]);
        }
      });
      
      // Re-attach event listeners to the new items
      const bannerComponent = currentBannerElement;
      if (bannerComponent && typeof bannerComponent.attachEventListeners === 'function') {
        bannerComponent.attachEventListeners();
      }
    }
  }

  renderProductGrid(html) {
    const innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById(this.productGridId).innerHTML;

    document.getElementById(this.productGridId).innerHTML = innerHTML;

    // Force a scroll to trigger show-on-scroll load in animations
    window.scrollBy(0, 1);
    window.scrollBy(0, -1);

    const event = new CustomEvent('paginate');
    document.dispatchEvent(event);
  }

  renderFilters(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetsSynced = this.syncFacetWrappers(parsedHTML);

    // Update only elements with data-filter-update attribute
    const elementsToUpdate = parsedHTML.querySelectorAll('[data-filter-update]');
    elementsToUpdate.forEach(newElement => {
      // The facet lists were just replaced wholesale — patching them again by key
      // would only re-apply what they already hold.
      if (facetsSynced && newElement.closest('.facets__wrapper')) return;

      // querySelectorAll, not querySelector: the active facets block is rendered
      // once per form (desktop and mobile drawer) under the same key, and only the
      // first copy used to be refreshed.
      const targetElements = document.querySelectorAll(`[data-filter-update="${newElement.getAttribute('data-filter-update')}"]`);
      targetElements.forEach(targetElement => {
        // Only update the inner content, preserving the container and its listeners
        targetElement.innerHTML = newElement.innerHTML;
        // Modifier classes are part of the server's answer too (e.g. the active
        // facets container marking that filters are applied), so carry them over.
        targetElement.className = newElement.className;
      });
    });

    this.hideImpliedFacets(this.renderedFilterParams);
    this.layoutValueGroups();
    this.renderActiveFacetFallback();
    this.syncSortByControls(parsedHTML);
  
    this.bindSortByEventListeners();
    this.bindActiveFacetButtonEvents();

    // Auto-close dropdowns on desktop after filter update.
    // Query the whole document (not just this form) so the search page's
    // "Result Type" disclosure — rendered as a sibling OUTSIDE
    // <collection-filtering-form> — also collapses after a selection.
    if (!this.isMobileViewport()) {
      const openDropDowns = document.querySelectorAll('details.facets__disclosure[open]');
      openDropDowns.forEach(dropdown => {
        // Guard: don't close if focused on a price range input
        const isPriceRange = dropdown.querySelector('price-range-selector');
        const focusedInDropdown = dropdown.contains(document.activeElement);
        const isPriceInputFocused = isPriceRange && focusedInDropdown && document.activeElement.tagName === 'INPUT';

        if (!isPriceInputFocused) {
          dropdown.removeAttribute('open');
        }
      });
    } else if (this.pendingMobileAutoClose) {
      // Mirror desktop: a Design/Color swatch selection goes straight to results
      // instead of leaving the drawer open for a manual close.
      this.pendingMobileAutoClose = false;
      this.handleDrawerClose();
    }
  }

  /**
   * Replace the facet lists wholesale instead of patching them value by value.
   *
   * Shopify drops zero-count values from `collection.filters`, and drops a whole
   * filter when none of its values match the rest of the selection (Designs
   * disappears entirely under Product type "ID Tag"). The [data-filter-update]
   * patching below keys nodes by their index inside the filter, so a shorter
   * response leaves the surplus values — and whole stale dropdowns — in the DOM,
   * still enabled and clickable. Swapping the wrapper keeps what is rendered
   * identical to what the server returned; open disclosures and focus are restored
   * afterwards, keyed by filter and by value rather than by position.
   *
   * Collection template only: the search page mutates its inputs' disabled state
   * from onSubmitHandler(), which a wholesale swap would throw away.
   */
  syncFacetWrappers(parsedHTML) {
    if (!this.collectionTemplate) return false;

    const currentWrappers = this.querySelectorAll('.facets__wrapper');
    const newWrappers = parsedHTML.querySelectorAll('.facets__wrapper');
    if (!currentWrappers.length || currentWrappers.length !== newWrappers.length) return false;

    currentWrappers.forEach((wrapper, index) => {
      const openFilterKeys = Array.from(wrapper.querySelectorAll('details.js-filter[open]'))
        .map(details => details.dataset.filterKey)
        .filter(Boolean);

      const focused = wrapper.contains(document.activeElement) ? document.activeElement : null;
      const focusedFacet = focused ? this.describeFacetInput(focused) : null;
      const caretPosition = focused && focused.type === 'text' ? focused.selectionStart : null;

      wrapper.innerHTML = newWrappers[index].innerHTML;

      wrapper.querySelectorAll('details.js-filter').forEach(details => {
        if (openFilterKeys.includes(details.dataset.filterKey)) details.setAttribute('open', '');
      });

      if (!focusedFacet) return;

      const toFocus = this.findFacetInput(wrapper, focusedFacet);
      if (!toFocus) return;

      toFocus.focus();
      if (caretPosition !== null && typeof toFocus.setSelectionRange === 'function') {
        toFocus.setSelectionRange(caretPosition, caretPosition);
      }
    });

    return true;
  }

  // Describe a facet input by what it filters rather than by where it sits: values
  // shift position whenever Shopify drops a zero-count value from the list.
  describeFacetInput(element) {
    if (element.tagName !== 'INPUT') return null;
    if (element.type === 'checkbox' && element.name) return { name: element.name, value: element.value };
    if (element.id) return { id: element.id };
    return null;
  }

  findFacetInput(wrapper, facet) {
    const inputs = Array.from(wrapper.querySelectorAll('input'));
    if (facet.id) return inputs.find(input => input.id === facet.id) || null;
    return inputs.find(input => input.name === facet.name && input.value === facet.value) || null;
  }

  /**
   * Rebuild the selected-filter chips from the URL when the response carried none.
   *
   * A selection that matches no product (Product type "Whelping Collars" plus a
   * Design that type is not made in) comes back with those values dropped from
   * `collection.filters` altogether, so `active_values` is empty and Liquid has
   * nothing to render chips from. Without chips there is no way to undo the last
   * click, and `.active-facets__button:only-child` would hide "Clear all" as well.
   */
  renderActiveFacetFallback() {
    const template = document.querySelector('template[data-active-facet-template]');
    if (!template) return;

    const params = new URLSearchParams(window.location.search);
    const selected = Array.from(params.entries()).filter(
      ([key]) => key.startsWith('filter.') && !key.startsWith('filter.v.price')
    );

    document.querySelectorAll('.active-facets').forEach(container => {
      container.querySelectorAll('[data-facet-fallback]').forEach(chip => chip.remove());

      // Server-rendered chips are always preferred — they carry the filter's own
      // label and Shopify's url_to_remove.
      if (!selected.length || container.querySelector('.js-facet-remove.button--tertiary')) return;

      selected.forEach(([key, value]) => {
        const remaining = Array.from(params.entries()).filter(([k, v]) => !(k === key && v === value));
        const search = new URLSearchParams(remaining).toString();
        const chip = template.content.firstElementChild.cloneNode(true);
        const label = chip.querySelector('[data-facet-fallback-label]');

        chip.href = `${window.location.pathname}${search ? `?${search}` : ''}`;
        chip.setAttribute('aria-label', (chip.getAttribute('aria-label') || '').replace('__value__', value));
        if (label) label.textContent = value;
        container.appendChild(chip);
      });
    });
  }

  syncSortByControls(parsedHTML) {
    ['#sort_by_input', '#SortBydesktop', '#SortBymobile'].forEach(selector => {
      const currentControl = this.querySelector(selector);
      const updatedControl = parsedHTML.querySelector(selector);

      if (currentControl && updatedControl) {
        currentControl.value = updatedControl.value;
      }
    });
  }

  bindSortByEventListeners() {
    this.sortByDesktop = this.querySelector('#SortBydesktop');
    this.sortByMobile = this.querySelector('#SortBymobile');

    if (this.sortByDesktop && this.sortByMobile) {
      // Remove existing listeners using bound functions
      this.sortByDesktop.removeEventListener('change', this.handleSortByChangeBound);
      this.sortByMobile.removeEventListener('change', this.handleSortByChangeBound);

      this.sortByDesktop.removeEventListener('input', this.stopInputPropagationBound);
      this.sortByMobile.removeEventListener('input', this.stopInputPropagationBound);

      // Re-attach event listeners
      this.sortByDesktop.addEventListener('change', this.handleSortByChangeBound);
      this.sortByMobile.addEventListener('change', this.handleSortByChangeBound);

      this.sortByDesktop.addEventListener('input', this.stopInputPropagationBound);
      this.sortByMobile.addEventListener('input', this.stopInputPropagationBound);
    }
  }

  bindActiveFacetButtonEvents() {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      // Remove existing listeners to prevent duplicates
      element.removeEventListener('click', this.onActiveFilterClick);
      // Add event listener
      element.addEventListener('click', this.onActiveFilterClick, { once: true });
    });
  }

  updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams ? '?'.concat(searchParams) : ''}`);
  }

  getSections() {
    const sections = [
      {
        id: this.productGridId,
        section: document.getElementById(this.productGridId).dataset.sectionId
      },
      {
        id: this.filterFormsId,
        section: document.getElementById('main-collection-filters').dataset.id
      }
    ];

    // Add collection grid banner section if it exists
    const collectionGridBanner = document.querySelector('collection-grid-banner');
    if (collectionGridBanner) {
      sections.push({
        id: 'CollectionGridBanner',
        section: collectionGridBanner.dataset.wethemeSectionId
      });
    }

    return sections;
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('popstate', this.onHistoryChangeBound);
    document.removeEventListener('click', this.onDropDownBlurBound);
    
    // Remove collection filter event listener
    if (window.eventBus) {
      window.eventBus.off('collection:filter:apply', this.handleCollectionFilterApply);
    }
  }
}

customElements.define('collection-filtering-form', CollectionFiltersForm);

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range-selector', PriceRange);

class PageType extends HTMLElement {
  constructor() {
    super()
    this.selector = document.querySelector('[data-page-type-selector]');
    this.selector.addEventListener('change', this.setValue);
  }

  setValue(e) {
    const selectedTypes = e.currentTarget.querySelectorAll('input:checked');
    const types = Array.from(selectedTypes).map(option => option.value).join(',');

    const typeInput = document.querySelector('#page-type-input');
    typeInput.value = types;

    const event = new Event('input');
    typeInput.closest('form').dispatchEvent(event);
  }
}

customElements.define('page-type-selector', PageType);
/******/ })()
;
